/**
 * No session replay on Flow pages (WP-49 part J, PLAN section 14.7).
 *
 * A Flow page shows what people said in a meeting: transcript lines, notes,
 * board content. Replay masks inputs and the `.ph-mask` / `.ph-block`
 * subtrees, but a recording of the page is a recording of the meeting, so
 * Flow's routes record nothing, as the join surfaces already do
 * (`join-surface-analytics.ts`). Event analytics carry on; only replay stops.
 *
 * Two pieces:
 *   - {@link isFlowReplayExcludedUrl}: the pure rule, which URLs are Flow.
 *   - {@link FlowReplayGate}: stops replay on entering one and resumes it on
 *     leaving, but ONLY if the gate itself stopped a running recording. A page
 *     load that never recorded (a join surface, a viewer who opted out, the
 *     sample rate) is never started by leaving Flow.
 *
 * {@link dropsReplaySnapshot} is the backstop: replay can start a moment after
 * the first navigation (its start waits on PostHog's configuration), so
 * `before_send` also drops any snapshot chunk sent while the page is Flow's.
 */

/**
 * Every Flow route: the whole `/flow` tree (meetings, sessions, live, boards,
 * recordings, projects, days, pages, insights, costs, import), the desktop
 * Flow widget, and `/m/<code>`, the join door for hosted meetings and
 * Flowboard links (`/m/<code>/board`). The legacy `/projects` and
 * `/knowledge` links redirect into `/flow`, so the URL after the redirect is
 * what counts. Accepts a path (`/flow/boards/x`), a hash (`#/flow/boards/x`)
 * or a whole URL; query and fragment are ignored.
 */
export function isFlowReplayExcludedUrl(url: string): boolean {
  let path = url.trim();
  const hash = path.indexOf('#/');
  if (hash >= 0) {
    path = path.slice(hash + 1);
  } else if (/^[a-z][a-z0-9+.-]*:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return false;
    }
  }
  path = (path.split(/[?#]/)[0] ?? '').toLowerCase();
  return /^\/(?:flow(?:-off)?(?:\/|$)|desktop\/flow-widget(?:\/|$)|m\/)/.test(path);
}

/** True for a replay snapshot chunk sent while the page is a Flow page. */
export function dropsReplaySnapshot(eventName: string, currentUrl: string): boolean {
  return eventName === '$snapshot' && isFlowReplayExcludedUrl(currentUrl);
}

/** What the gate needs of PostHog. All four are cheap and idempotent. */
export interface FlowReplayHost {
  /** Is a recording running right now? */
  recording(): boolean;
  stop(): void;
  start(): void;
  /** Has the viewer opted out of analytics? */
  optedOut(): boolean;
}

export class FlowReplayGate {
  private pausedByFlow = false;

  constructor(private readonly host: FlowReplayHost) {}

  /** Call on every navigation, with the URL after redirects. */
  onNavigate(url: string): void {
    const excluded = isFlowReplayExcludedUrl(url);
    if (excluded) {
      if (!this.pausedByFlow && this.host.recording()) {
        this.host.stop();
        this.pausedByFlow = true;
      }
      return;
    }
    if (!this.pausedByFlow) return;
    this.pausedByFlow = false;
    // An opt-out made while on Flow is honoured: nothing is resumed.
    if (!this.host.optedOut()) this.host.start();
  }
}
